import {
	AddressType,
	BrandFileType,
	useDeleteBrandMutation,
	useDeleteOrganizationAddressMutation,
	useGetAllBrandsQuery,
	useGetAllClientOrdersQuery,
	useGetAllProductsQuery,
	useGetClientAllOrderQuoteQuery,
	useGetOrganizationAddressesQuery,
	useGetOrganizationQuery,
	useUpdateOrganizationAddressMutation,
} from '@codezo/graphql';
import {
	ClientBrands,
	ClientCallout,
	ClientDetails,
	ClientHero,
	ClientOverview,
	ClientSections,
	scrollToSection,
	type ClientAddress,
	type ClientBrandCard,
	type ClientFact,
	type ClientLayoutSection,
	type ClientMetric,
} from '@codezo/client-detail';
import {
	ArrowUpRight,
	FileText,
	Loader2,
	MoreHorizontal,
	Palette,
	Pencil,
	Plus,
	Star,
	Trash2,
	Truck,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { useAuth } from '../../hooks/useAuth';
import { ADDRESS_TYPE_LABEL, type OrganizationAddress } from '../../lib/address-utils';
import { formatMonthYear } from '../../lib/utils';
import { AddressModal } from '../settings/AddressModal';
import { CreateBrandModal } from '../brands/CreateBrandModal';

/**
 * The organization's own account, as one document with an index rail — the
 * client-side twin of the admin client page, built from the same package.
 *
 * ── Why this replaced the brands list ──
 *
 * "Brands" was a nav item that led to a grid of cards and nothing else, while
 * the things that describe the account — who we are, where we bill, where we
 * ship — were buried in a Settings tab. Reversing that is the whole change: the
 * account is the page, and the brand kits are a section of it.
 *
 * ── What is deliberately NOT here ──
 *
 * Contacts and Settings, which the admin page carries. This app already has
 * `Users` and `Settings` as their own nav items with their own forms; a second
 * copy here would be two places to edit one thing. The company facts are shown
 * read-only for the same reason, with "Edit in settings" pointing at the form
 * that owns them.
 *
 * Addresses are the exception and are fully editable here: they are half of
 * "Company & addresses", and splitting the grid from its own add button reads
 * as broken rather than as restraint. It is the same mutation the settings card
 * calls, so the two stay in step.
 *
 * The logo is read-only. Changing it is an upload flow (pick, crop, PUT two
 * blobs, persist) that this app has never offered for the organization mark,
 * and inventing one is not part of showing the account.
 */

/** The counts only — a page's worth of rows would be fetched and thrown away. */
const COUNT_ONLY = { limit: 1, currentPage: 1 };

/** The sections that live on this page. Any other metric key is a nav target. */
const SECTION_IDS = new Set(['overview', 'details', 'brands']);

/** Drops the blanks and puts a separator between what is left — the string
 * version of this could not carry a mono span for the registration number. */
function joinMeta(parts: ReactNode[]): ReactNode {
	const shown = parts.filter(Boolean);
	return shown.map((part, i) => (
		<span key={i}>
			{i > 0 && ' · '}
			{part}
		</span>
	));
}

export function ProfilePage() {
	const navigate = useNavigate();
	const { hash } = useLocation();
	const { user } = useAuth();
	const organizationId = user?.organizationId || '';
	const revealRef = useRef<((sectionId: string) => void) | null>(null);

	const [addressModalOpen, setAddressModalOpen] = useState(false);
	const [editingAddress, setEditingAddress] = useState<OrganizationAddress | null>(null);
	const [deleteAddressTarget, setDeleteAddressTarget] = useState<OrganizationAddress | null>(
		null
	);
	const [createBrandOpen, setCreateBrandOpen] = useState(false);
	const [deleteBrandTarget, setDeleteBrandTarget] = useState<{ id: string; name: string } | null>(
		null
	);

	const [updateAddress] = useUpdateOrganizationAddressMutation();
	const [deleteAddress, { loading: deletingAddress }] = useDeleteOrganizationAddressMutation();
	const [deleteBrand, { loading: deletingBrand }] = useDeleteBrandMutation();

	const { data: orgData, loading: orgLoading } = useGetOrganizationQuery({
		variables: { id: organizationId },
		fetchPolicy: 'no-cache',
		skip: !organizationId,
	});
	const org = orgData?.getOrganization;

	/* Addresses have their own query rather than riding on `getOrganization`:
	   it is the one `AddressModal` and the settings card are typed against, so
	   the three stay in step by construction. */
	const { data: addressData, refetch: refetchAddresses } = useGetOrganizationAddressesQuery({
		variables: { organizationId },
		skip: !organizationId,
		fetchPolicy: 'no-cache',
	});

	const {
		data: brandData,
		loading: brandsLoading,
		refetch: refetchBrands,
	} = useGetAllBrandsQuery({
		variables: { input: { pagination: { limit: 50, currentPage: 1 } } },
		fetchPolicy: 'no-cache',
	});

	/* The three counts the overview strip carries. Each is the client-scoped
	   list query the matching nav page uses, asked for one row — the figure
	   lives in `pageInfo`, and fetching a page to count it would be waste. */
	const { data: productData } = useGetAllProductsQuery({
		variables: { input: { pagination: COUNT_ONLY } },
		fetchPolicy: 'no-cache',
	});
	const productTotal = productData?.getAllProduct?.pageInfo?.totalCount ?? 0;

	const { data: quoteData } = useGetClientAllOrderQuoteQuery({
		variables: { input: { pagination: COUNT_ONLY } },
		fetchPolicy: 'no-cache',
	});
	const quoteTotal = quoteData?.getClientAllOrderQuote?.pageInfo?.totalCount ?? 0;

	const { data: orderData } = useGetAllClientOrdersQuery({
		variables: { input: { pagination: COUNT_ONLY } },
		fetchPolicy: 'no-cache',
	});
	const orderTotal = orderData?.getAllClientOrders?.pageInfo?.totalCount ?? 0;

	/* ── brands → cards ── */
	const brandCards: ClientBrandCard[] = useMemo(
		() =>
			(brandData?.getAllBrand?.data ?? []).map((brand) => {
				const palette = brand.colorPalette;
				const colors = palette
					? [
							...(palette.primary ?? []),
							...(palette.secondary ?? []),
							...(palette.accent ?? []),
							...(palette.neutral ?? []),
						].map((c) => c.hex)
					: [];
				const fonts = [
					brand.typography?.primaryFont,
					brand.typography?.secondaryFont,
				].filter((f): f is string => Boolean(f));
				return {
					id: brand.id,
					name: brand.name,
					code: brand.name.slice(0, 2).toUpperCase(),
					kind: brand.description ?? null,
					colors,
					fonts,
					fileCount: brand.brandFiles?.length ?? 0,
					updatedLabel: brand.updatedAt ? formatMonthYear(brand.updatedAt) : null,
					status: brand.brandFiles?.some((f) => f.type === BrandFileType.Logo)
						? null
						: { label: 'No logo', tone: 'warn' as const },
				};
			}),
		[brandData]
	);

	/* The hero has no palette of its own — it borrows the account's brands', one
	   named column each. Capped at six so the names stay readable; past that the
	   Brands section is the place to see the rest. */
	const heroBrands = useMemo(
		() =>
			brandCards
				.slice(0, 6)
				.map((b) => ({ name: b.name, colors: (b.colors ?? []).slice(0, 4) })),
		[brandCards]
	);

	/* ── company facts ──
	   Read-only: the form that owns these lives in Settings, and a second copy
	   of it here would be two places to change one field. */
	const registrations = org?.businessRegistrations ?? [];
	/* The company number, not the VAT one where both exist: VAT is derived from
	   it in most countries, and the registration is what gets quoted back. */
	const primaryRegistration = registrations.find((r) => r.type !== 'VAT') ?? registrations[0];

	const facts: ClientFact[] = org
		? [
				{ label: 'Legal name', value: org.name },
				...registrations.map((reg) => ({
					label: reg.type,
					value: reg.number,
					mono: true,
				})),
				{ label: 'Website', value: org.websiteUrl },
				{ label: 'Industry', value: org.organizationIndustry?.name },
				{ label: 'Country', value: org.countryCode },
				{ label: 'Currency', value: org.organizationSetting?.currency, mono: true },
			]
		: [];

	/* Primary first — the one new quotes default to should be the one read
	   first, the same order the settings card uses. */
	const addressRecords = [...(addressData?.getOrganizationAddresses ?? [])].sort(
		(a, b) => Number(b.isPrimary) - Number(a.isPrimary)
	);
	const addressCards: ClientAddress[] = addressRecords.map((address) => ({
		id: address.id,
		kind: ADDRESS_TYPE_LABEL[address.addressType],
		label: address.name,
		lines: [
			[address.addressLine1, address.addressLine2].filter(Boolean).join(', '),
			[address.postalCode, address.city].filter(Boolean).join(' '),
			[address.stateCode, address.countryCode].filter(Boolean).join(' · '),
		].filter(Boolean),
		isPrimary: address.isPrimary ?? false,
		/* Billing reads stronger than the rest — "where does the invoice go" is
		   what someone scans this grid for. */
		emphasis: address.addressType === AddressType.Billing,
	}));

	/** Promotion is an ordinary update — `isPrimary` is a field on the address,
	 *  so the whole record goes back unchanged apart from it. */
	const handleSetPrimaryAddress = async (address: OrganizationAddress) => {
		try {
			await updateAddress({
				variables: {
					id: address.id,
					input: {
						addressLine1: address.addressLine1,
						addressLine2: address.addressLine2 ?? undefined,
						city: address.city,
						stateCode: address.stateCode ?? undefined,
						postalCode: address.postalCode,
						countryCode: address.countryCode,
						googlePlaceId: address.googlePlaceId ?? undefined,
						latitude: address.latitude ?? undefined,
						longitude: address.longitude ?? undefined,
						name: address.name ?? undefined,
						addressType: address.addressType,
						isPrimary: true,
					},
				},
			});
			toast.success('Primary address updated');
			refetchAddresses();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update primary address');
		}
	};

	const handleDeleteAddress = async () => {
		if (!deleteAddressTarget) return;
		try {
			await deleteAddress({ variables: { id: deleteAddressTarget.id } });
			toast.success('Address removed');
			setDeleteAddressTarget(null);
			refetchAddresses();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to remove address');
		}
	};

	const handleDeleteBrand = async () => {
		if (!deleteBrandTarget) return;
		try {
			await deleteBrand({ variables: { input: { id: deleteBrandTarget.id } } });
			toast.success('Brand deleted');
			setDeleteBrandTarget(null);
			refetchBrands();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to delete brand');
		}
	};

	/* A brand page's back link points at `/profile#brands` — landing at the top
	   of a page whose middle you just came from reads as having lost your place. */
	useEffect(() => {
		if (!hash) return;
		const id = hash.slice(1);
		if (!SECTION_IDS.has(id)) return;
		/* After the sections have rendered, not before — the anchor does not
		   exist on the first paint of a page still waiting on its queries. */
		const timer = window.setTimeout(() => scrollToSection(id), 0);
		return () => window.clearTimeout(timer);
	}, [hash, orgLoading, brandsLoading]);

	/* Products, quotes and orders are counted here but live on their own nav
	   pages, so those cells navigate; the rest jump within this one. */
	const metrics: ClientMetric[] = [
		{ label: 'Brands', value: brandCards.length, sectionId: 'brands' },
		{ label: 'Products', value: productTotal, sectionId: 'products' },
		{ label: 'Quotes', value: quoteTotal, sectionId: 'quotes' },
		{ label: 'Orders', value: orderTotal, sectionId: 'orders' },
		{ label: 'Addresses', value: addressRecords.length, sectionId: 'details' },
	];

	const handleMetricClick = (sectionId: string) => {
		if (SECTION_IDS.has(sectionId)) revealRef.current?.(sectionId);
		else navigate(`/${sectionId}`);
	};

	if (orgLoading) {
		return (
			<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
				<div className="flex min-h-[50vh] items-center justify-center">
					<Loader2 className="animate-spin text-text-4" size={20} />
				</div>
			</div>
		);
	}

	if (!org) {
		return (
			<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
				<div className="py-16 text-center text-sm text-text-3">
					Your organization could not be loaded.
				</div>
			</div>
		);
	}

	const sections: ClientLayoutSection[] = [
		{
			id: 'overview',
			label: 'Overview',
			content: (
				<ClientOverview
					metrics={metrics}
					onMetricClick={handleMetricClick}
					callout={
						<ClientCallout
							icon={<Palette size={15} />}
							title={
								brandCards.length > 0 ? 'Brand kits in use' : 'No brand kits yet'
							}
							body={
								brandCards.length > 0
									? `${brandCards.length} kit${brandCards.length === 1 ? '' : 's'} · colours, fonts and logos your quotes and products are made against`
									: 'Create a brand kit to keep your colours, fonts and logos in one place — products and quotes are made against it.'
							}
							action={
								brandCards.length === 0 ? (
									<Button
										variant="outline"
										size="sm"
										onClick={() => setCreateBrandOpen(true)}
									>
										<Plus size={12} data-icon="inline-start" />
										New brand
									</Button>
								) : undefined
							}
						/>
					}
				/>
			),
		},
		{
			id: 'details',
			label: 'Company & addresses',
			content: (
				<ClientDetails
					facts={facts}
					addresses={addressCards}
					action={
						<Button
							variant="outline"
							size="sm"
							onClick={() => navigate('/settings?tab=general')}
						>
							Edit in settings
						</Button>
					}
					renderAddressIcon={(address) =>
						address.kind === ADDRESS_TYPE_LABEL[AddressType.Shipping] ? (
							<Truck size={13} />
						) : (
							<FileText size={13} />
						)
					}
					renderAddressAction={(address) => {
						const record = addressRecords.find((a) => a.id === address.id);
						if (!record) return null;
						/* The primary address is what quotes and orders default to;
						   removing it silently would leave them without one. */
						const blockedFromDelete =
							Boolean(record.isPrimary) && addressRecords.length > 1;
						return (
							<DropdownMenu>
								<DropdownMenuTrigger
									aria-label="Address actions"
									className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-[var(--r-2)] text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
								>
									<MoreHorizontal size={13} />
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									<DropdownMenuItem
										onClick={() => {
											setEditingAddress(record);
											setAddressModalOpen(true);
										}}
									>
										<Pencil size={14} /> Edit
									</DropdownMenuItem>
									{!record.isPrimary && (
										<DropdownMenuItem
											onClick={() => handleSetPrimaryAddress(record)}
										>
											<Star size={14} /> Set as primary
										</DropdownMenuItem>
									)}
									<DropdownMenuSeparator />
									<DropdownMenuItem
										variant="destructive"
										disabled={blockedFromDelete}
										onClick={() => setDeleteAddressTarget(record)}
									>
										<Trash2 size={14} />{' '}
										{blockedFromDelete
											? 'Set another as primary first'
											: 'Remove'}
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						);
					}}
					addAddressSlot={
						<button
							type="button"
							onClick={() => {
								setEditingAddress(null);
								setAddressModalOpen(true);
							}}
							className="flex min-h-[9.5rem] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-[var(--r-3)] border border-dashed border-line-2 bg-transparent text-xs text-text-3 transition-colors hover:border-line-3 hover:text-text-1"
						>
							<Plus size={14} />
							Add address
						</button>
					}
				/>
			),
		},
		{
			id: 'brands',
			label: 'Brands',
			count: brandCards.length,
			content: (
				<ClientBrands
					brands={brandCards}
					meta={
						brandCards.length > 0
							? `${brandCards.length} kit${brandCards.length === 1 ? '' : 's'}`
							: undefined
					}
					action={
						<Button
							variant="outline"
							size="sm"
							onClick={() => setCreateBrandOpen(true)}
						>
							<Plus size={12} data-icon="inline-start" />
							New brand
						</Button>
					}
					emptyHint="Your colours, fonts and logos live in a brand kit. Products and quotes are made against it."
					emptyAction={
						<Button size="sm" onClick={() => setCreateBrandOpen(true)}>
							<Plus size={12} data-icon="inline-start" />
							New brand
						</Button>
					}
					brandHref={(brand) => `/brands/${brand.id}`}
					renderBrandAction={(brand) => (
						<div className="flex items-center gap-1.5">
							<Button
								variant="outline"
								size="sm"
								onClick={() => navigate(`/brands/${brand.id}`)}
							>
								<ArrowUpRight size={12} data-icon="inline-start" />
								Open
							</Button>
							<DropdownMenu>
								<DropdownMenuTrigger
									aria-label="Brand actions"
									className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-[var(--r-2)] text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
								>
									<MoreHorizontal size={13} />
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									<DropdownMenuItem
										variant="destructive"
										onClick={() =>
											setDeleteBrandTarget({ id: brand.id, name: brand.name })
										}
									>
										<Trash2 size={14} /> Delete brand
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						</div>
					)}
				/>
			),
		},
	];

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8 md:pt-8">
			<ConfirmDialog
				open={!!deleteAddressTarget}
				onOpenChange={(v) => !v && setDeleteAddressTarget(null)}
				title="Remove address"
				description={`${deleteAddressTarget?.name || deleteAddressTarget?.addressLine1} will no longer be selectable on quotes and orders.`}
				confirmLabel="Remove"
				loading={deletingAddress}
				onConfirm={handleDeleteAddress}
			/>

			<ConfirmDialog
				open={!!deleteBrandTarget}
				onOpenChange={(v) => !v && setDeleteBrandTarget(null)}
				title="Delete brand"
				description={`${deleteBrandTarget?.name} and its colours, fonts and files will be permanently removed.`}
				confirmLabel="Delete"
				loading={deletingBrand}
				onConfirm={handleDeleteBrand}
			/>

			<AddressModal
				open={addressModalOpen}
				onOpenChange={setAddressModalOpen}
				organizationId={organizationId}
				address={editingAddress}
				isFirstAddress={addressRecords.length === 0}
				onSuccess={() => {
					refetchAddresses();
					setAddressModalOpen(false);
					setEditingAddress(null);
				}}
			/>

			<CreateBrandModal
				open={createBrandOpen}
				onOpenChange={setCreateBrandOpen}
				onSuccess={() => refetchBrands()}
			/>

			<ClientHero
				name={org.name}
				code={org.name.slice(0, 2).toUpperCase()}
				logoUrl={org.logo}
				brands={heroBrands}
				/* The registration is the one identifier people quote back, so it
				   rides in the subline rather than only in the facts grid below.
				   Nodes, not a joined string: the number wants mono. */
				meta={joinMeta([
					org.organizationIndustry?.name,
					org.countryCode,
					primaryRegistration && (
						<>
							{primaryRegistration.type}{' '}
							<span className="font-mono">{primaryRegistration.number}</span>
						</>
					),
					org.createdAt ? `client since ${formatMonthYear(org.createdAt)}` : null,
				])}
			/>

			<ClientSections sections={sections} railLabel="Profile" revealRef={revealRef} />
		</div>
	);
}
